import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { SiteUserSettingsSubspaceCtrl } from '../../../ts/controllers/SiteUserSettingsSubspaceCtrl';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { ServerUsers } from '../../../ts/resources/ServerUsers';
import { IGetUserSettingsResult } from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Subspace } from '../TabbedSpace';

export function siteUserSettingsSubspace(params: Omit<SiteUserSettingsContentOwnProps, 'renderSubspace'>): Subspace {
  return {
    name: 'settings',
    tabLabel: Messages.settings(),
    shouldRenderTabContentCount: false,
    renderContent: () => (
      <SiteUserSettingsContent {...{...params, renderSubspace: settingsSubspace}} />
    )
  };
}

const template = `<div ng-controller="SiteUserSettingsSubspaceCtrl" ng-include="'siteUserSettingsSubplace.html'"></div>`;
const settingsSubspace = (props: AngularDirective.Props): JSX.Element => {
  // the parent div here is important to ensure AngularDirective plays nice when unmounting
  return (
    <div>
      <AngularDirective
        template={template}
        angularContext={props.angularContext}
        setupScope={props.setupScope}
      />
    </div>
  );
};

export interface SiteUserSettingsContentOwnProps {
  readonly angularContext: AngularContext;
  readonly isSessionUserSelf: boolean;
  readonly userSettings: IGetUserSettingsResult;
  readonly siteUser: ISiteUserInfo;
  readonly urls: SpacesSiteUrls;
  readonly renderSubspace: (props: AngularDirective.Props) => JSX.Element;
}

export interface SiteUserSettingsContentPropsFromAngular {
  readonly serverUsers: ServerUsers;
}

export type SiteUserSettingsContentProps = SiteUserSettingsContentOwnProps & SiteUserSettingsContentPropsFromAngular;

export interface SiteUserSettingsContentState {
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly serverUser: IServerUserInfo;
  readonly userRefreshTokenCount: number;
}

export class SiteUserSettingsContentComponent extends React.PureComponent<SiteUserSettingsContentProps, SiteUserSettingsContentState> {

  private spaceScope: SiteUserSettingsSubspaceCtrl.Scope = null;
  private unmounted: boolean = false;

  public constructor(props: SiteUserSettingsContentProps) {
    super(props);
    this.state = {
      renderChildren: false,
      hasError: _.isUndefined(this.props.userSettings),
      serverUser: undefined,
      userRefreshTokenCount: undefined
    };
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRenderNonError()) {
      return this.renderSubSpace();
    } else if (this.state.hasError) {
      return <RouterError errorType={'siteuser'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount() {
    if (!_.isUndefined(this.props.userSettings)) {
      this.getServerUser();
      this.getUserRefreshTokenCount();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!this.haveAllDataNeededToRenderNonError()) {
      return;
    }

    if (!this.state.renderChildren) {
      this.setState({renderChildren: true}, () => {
        if (this.spaceScope) {
          this.spaceScope.$apply();
        }
      });
    }
  }

  private updateState<K extends keyof SiteUserSettingsContentState>(newState: Pick<SiteUserSettingsContentState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getServerUser = async () => {
    try {
      const serverUser = await this.props.serverUsers.getUserByUsernameAndDomain(this.props.siteUser.username, this.props.siteUser.domainName);
      this.updateState({serverUser});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getUserRefreshTokenCount = async () => {
    try {
      const userRefreshTokenCount = await this.props.serverUsers.getUserRefreshTokenCount(this.props.siteUser.username, this.props.siteUser.domainName);
      this.updateState({userRefreshTokenCount});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private haveAllDataNeededToRenderNonError() {
    return !this.state.hasError && !_.isUndefined(this.state.serverUser) && !_.isUndefined(this.state.userRefreshTokenCount);
  }

  private renderSubSpace(): JSX.Element {
    return this.props.renderSubspace({
      angularContext: this.props.angularContext,
      setupScope: this.setupScope
    });
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as SiteUserSettingsSubspaceCtrl.Scope;
    this.spaceScope = childScope;
    childScope.isSessionUserSelf = this.props.isSessionUserSelf;
    childScope.userSettings = this.props.userSettings;
    childScope.userRefreshTokenCount = this.state.userRefreshTokenCount;
    childScope.serverUser = this.state.serverUser;
    childScope.siteUser = this.props.siteUser;
    return childScope;
  }
}

export namespace SiteUserSettingsContentAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      serverUsers: ServiceLocator.get<ServerUsers>('ServerUsers'),
    };
  }

  function selectPropsFromAngularServices(angularServices: SiteUserSettingsContentPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<SiteUserSettingsContentOwnProps, SiteUserSettingsContentPropsFromAngular, SiteUserSettingsContentPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const SiteUserSettingsContent: React.ComponentType<SiteUserSettingsContentOwnProps> = SiteUserSettingsContentAngularProvider.HOC(SiteUserSettingsContentComponent);
